import { NotAuthenticatedException } from "@/utils/notAuthenticatedException";
import { toast } from "vue3-toastify";

export type FetchOptions = {
    queryParams ?: URLSearchParams,
    formdata    ?: boolean,
    jsonPayload ?: boolean,
    jsonResponse?: boolean,
    authType    ?: 'cookies'|'bearer-auth'|'auth',
    authToken   ?: string,
    returnBooleanForLogin?: boolean,
    doNotRedirectOn401?: boolean,
    resolve404AsValue?: boolean,
    resolve404As?: unknown,
    abtSignal?: AbortSignal
}
const defaultFetchOptions = { jsonPayload: true, jsonResponse: true, authType: 'cookies' } as FetchOptions;

export class FetchUtil {
    constructor(public baseUrl: string|Promise<string>) { }

    private static readonly _inflight = new Map<string, Promise<unknown>>();

    private async fetch(method: string, basePath: string|Promise<string>, path: string, payload: object = {}, options?: FetchOptions, authToken?: string, isRefreshing = false): Promise<unknown> {
        basePath = await basePath;
        options = { ...defaultFetchOptions, ...options }
        // console.log(method, basePath, path, payload, options);
        // this.logger.debug(`[fetch] (method) basePath -> path | options`);
        // this.logger.debug(`[fetch] (${method}) ${basePath} -> ${path} | `, options);

        const cleanPath = (path.startsWith('/')) ? path.slice(1, Infinity) : path;
        let resolvedUrl = `${basePath}/${cleanPath}`;

        // this.logger.debug(`[fetch] method == 'GET' && options?.queryParams | 1, 2, 1 && 2`, method == 'GET', options?.queryParams, method == 'GET' && options?.queryParams)

        if (method == 'GET' && (options?.queryParams || Object.entries(payload).length)) {
            if (!resolvedUrl.endsWith('/')) resolvedUrl = resolvedUrl + '/';
            resolvedUrl = resolvedUrl + '?' + (options?.queryParams?.toString() ?? new URLSearchParams(Array.from(Object.entries(payload)).filter(e => e[1] !== undefined) as Record<string, any>).toString());
            // this.logger.debug(`[fetch] resolvedUrl`, options.queryParams.toString())
            // this.logger.debug(`[fetch] resolvedUrl`, resolvedUrl)
        }

        const headers = new Headers();
        if (authToken && (options?.authType == "bearer-auth" || options?.authType == "auth"))
            headers.append('Authorization' , (options?.authType == "auth" ? '' : 'Bearer ') + authToken);
        if (options?.jsonPayload) headers.append('Content-Type', 'application/json');
        const credentials = options?.authType == "cookies" ? 'include' : undefined;

        const formData = new FormData();
        if (options?.formdata) {
            Array.from(Object.entries(payload))
                .forEach(([key, value]) => {
                    if (value instanceof File) formData.append(key, value);
                    else if (Array.isArray(value) && value.every(v => v instanceof File)) value.forEach(v => formData.append(key, v as File));
                    else formData.append(key, (value instanceof Array || value instanceof Object)? JSON.stringify(value) : value as string)
                    }
                )
        }

        const body = method == 'GET' ? undefined
        : options?.queryParams ? options.queryParams
        : options?.formdata ? formData
        : options?.jsonPayload ? JSON.stringify(payload) : null;

        // const reqID = getRandomString(5, true);
        // console.debug(`[fetch] [${reqID}] fetching => ${resolvedUrl}`, {method, headers, body, credentials});
        const response = await fetch(resolvedUrl, {method, headers, body, credentials, signal: options?.abtSignal ?? undefined});

        // Conta congelada: o backend responde 403 com error CONTA_CONGELADA em login,
        // refresh e rotas autenticadas. Intercepta ANTES do fluxo de refresh (que
        // engoliria o body) e direciona para a tela de conta congelada.
        if (response.status == 403) {
            const congeladaBody = await response.clone().json().catch(() => null) as { error?: string } | null;
            if (congeladaBody?.error === 'CONTA_CONGELADA') {
                if (!window.location.pathname.includes('conta-congelada'))
                    window.location.href = '/conta-congelada';
                throw congeladaBody;
            }
        }

        if (response.status == 401 || (response.status == 403 && !isRefreshing)) {
            // console.trace('[401 GOT] is refreshing?', isRefreshing);

            if (isRefreshing) {
                if (!options?.doNotRedirectOn401 && !options?.returnBooleanForLogin) {
                    if (window.location.href.includes('admin'))
                        window.location.href = '/admin';
                    else window.location.href = '/';
                }
                if (options?.returnBooleanForLogin) return false;
                else throw new NotAuthenticatedException();
            } else {
                try {
                    await FetchUtil.refresh();
                } catch (refreshError) {  
                    if (!options?.doNotRedirectOn401 && !options?.returnBooleanForLogin) {
                        const refreshMessage = (refreshError as Error)?.message;
                        if (refreshMessage) toast.error(refreshMessage);
                        if (window.location.href.includes('admin'))
                             window.location.href = '/admin';
                        else window.location.href = '/';
                    }
                    if (options?.returnBooleanForLogin) return false;
                    throw new NotAuthenticatedException();
                }
                return await this.fetch(method, basePath, path, payload, options, authToken, true);
            }
        } else if (options?.returnBooleanForLogin) return true;

        if (response.status == 404 && options?.resolve404AsValue) {
            return options?.resolve404As;
        }

        if (options?.jsonResponse) {
            if (response.status > 299) {
                // this.logger.warn(`[fetch] [${reqID}] failed`);
                throw (await response.json());
            }
            // this.logger.debug(`[fetch] [${reqID}] ok`);
            try {
                return await response.json();
            } catch (e) {
                console.groupCollapsed('[FetchUtils] expected error 201.json');
                console.warn('tentando dar .json em um content-none', e);
                console.groupEnd();
                return;
            }
        }
        else return response;
    }

    public static async refresh() {
        console.debug('[refresh] trying to refresh')
        const isAdminRoute = window.location.pathname.includes('admin');
        const serverUrl = window.configs.SERVER_URL_PROD; // eslint-disable-line
        const refreshUrl = `${serverUrl}/${isAdminRoute ? 'admin-auth' : 'user-auth'}/refresh-token`;
        const res = await fetch(refreshUrl, {method: 'POST', credentials: "include"});

        if (res.ok) return console.debug('[refresh] token refreshed successfully');
        const body = await res.text().catch(() => '');
        console.error('[refresh] refresh failed =>', body);
        let parsedMessage: string | undefined;
        try { parsedMessage = (JSON.parse(body) as { message?: string })?.message; } catch { /* corpo não é JSON */ }
        throw new NotAuthenticatedException(parsedMessage || 'Sua sessão expirou. Faça login novamente.');
    }

    async get(path: string, payload?: object, options?: FetchOptions, authToken?: string) {
        if (options?.abtSignal) {
            return await this.fetch('GET', this.baseUrl, path, payload, options, authToken);
        }

        const baseUrl = await this.baseUrl;
        const payloadStr = payload ? JSON.stringify(Object.entries(payload).sort()) : '';
        const paramsStr = options?.queryParams?.toString() ?? '';
        const key = [baseUrl, path, payloadStr, paramsStr, authToken ?? ''].join('|');

        const existing = FetchUtil._inflight.get(key);
        if (existing) return existing;

        const promise = this.fetch('GET', this.baseUrl, path, payload, options, authToken);
        FetchUtil._inflight.set(key, promise);
        promise.finally(() => { if (FetchUtil._inflight.get(key) === promise) FetchUtil._inflight.delete(key); });
        return promise;
    }

    async post(path: string, payload?: object, options?: FetchOptions, authToken?: string) {
        return await this.fetch('POST', this.baseUrl, path, payload, options, authToken);
    }

    async put(path: string, payload?: object, options?: FetchOptions, authToken?: string) {
        return await this.fetch('PUT', this.baseUrl, path, payload, options, authToken);
    }

    async patch(path: string, payload?: object, options?: FetchOptions, authToken?: string) {
        return await this.fetch('PATCH', this.baseUrl, path, payload, options, authToken);
    }

    async delete(path: string, payload?: object, options?: FetchOptions, authToken?: string) {
        return await this.fetch('DELETE', this.baseUrl, path, payload, options, authToken);
    }
}
